import { useMemo } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { enUS, tr } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import type { TrendPoint } from '@/api/analytics';

interface Props {
  data: TrendPoint[];
  metric: 'weight' | 'bmi';
  bmiBand?: { min: number; max: number } | null;
  unitLabel: string;
}

export function TrendChart({ data, metric, bmiBand, unitLabel }: Props) {
  const { i18n } = useTranslation();
  const locale = i18n.language?.startsWith('tr') ? tr : enUS;

  const formatted = useMemo(
    () =>
      data.map((p) => ({
        ...p,
        label: (() => {
          try {
            return format(parseISO(p.date), 'd MMM', { locale });
          } catch {
            return p.date;
          }
        })(),
      })),
    [data, locale]
  );

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-sm text-slate-500">
        No data in this range.
      </div>
    );
  }

  const values = data.map((d) => d.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const pad = (max - min) * 0.15 || 1;

  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={formatted} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={`fill-${metric}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#0ea5e9" stopOpacity={0.45} />
              <stop offset="100%" stopColor="#0ea5e9" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.2)" />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: 'currentColor' }}
            stroke="rgba(148,163,184,0.5)"
            interval="preserveStartEnd"
            minTickGap={20}
          />
          <YAxis
            domain={[Math.floor(min - pad), Math.ceil(max + pad)]}
            tick={{ fontSize: 11, fill: 'currentColor' }}
            stroke="rgba(148,163,184,0.5)"
            width={40}
          />
          <Tooltip
            contentStyle={{
              borderRadius: 8,
              border: '1px solid rgba(148,163,184,0.3)',
              fontSize: 12,
            }}
            formatter={(v: number) => [`${v.toFixed(1)} ${unitLabel}`, metric === 'weight' ? 'Weight' : 'BMI']}
          />
          {metric === 'bmi' && bmiBand && (
            <>
              <ReferenceLine
                y={bmiBand.min}
                stroke="#10b981"
                strokeDasharray="4 4"
                ifOverflow="extendDomain"
                label={{ value: 'Normal', position: 'insideTopRight', fill: '#10b981', fontSize: 10 }}
              />
              <ReferenceLine y={bmiBand.max} stroke="#f59e0b" strokeDasharray="4 4" ifOverflow="extendDomain" />
            </>
          )}
          <Area
            type="monotone"
            dataKey="value"
            stroke="#0ea5e9"
            strokeWidth={2}
            fill={`url(#fill-${metric})`}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
